import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Mail, Lock, User, Loader2, ArrowRight, ShieldCheck, Heart } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import CustomCursor from "../components/CustomCursor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

export default function LoginPage() {
  const navigate = useNavigate();
  const { user, setUser, login: googleLogin, mergeCart } = useAuth();
  const { language } = useLanguage();
  const id = language === "id";

  const [portal, setPortal] = useState("customer"); // "customer" | "staff"
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      const isAdmin = user.role === "super_admin" || user.role === "florist_admin";
      navigate(isAdmin ? "/admin" : "/home", { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async () => {
    if (!form.email || !form.password) {
      toast.error(id ? "Email dan kata sandi wajib diisi" : "Email and password are required");
      return;
    }
    if (portal === "customer" && mode === "register" && form.password.length < 6) {
      toast.error(id ? "Kata sandi minimal 6 karakter" : "Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      const endpoint = (portal === "customer" && mode === "register") ? "/auth/register" : "/auth/login";
      const payload = (portal === "customer" && mode === "register")
        ? { email: form.email, password: form.password, name: form.name }
        : { email: form.email, password: form.password };
      const res = await axios.post(`${API}${endpoint}`, payload, { withCredentials: true });
      setUser(res.data);
      // Merge anonymous cart into user cart after login
      await mergeCart();

      if (portal === "staff") {
        const isAdmin = res.data.role === "super_admin" || res.data.role === "florist_admin";
        if (!isAdmin) {
          toast.error(id ? "Akun ini bukan staff. Gunakan portal pelanggan." : "This account is not staff. Use the customer portal.");
          await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
          setUser(null);
          setLoading(false);
          return;
        }
        toast.success(id ? `Selamat datang, ${res.data.name || "Staff"}` : `Welcome, ${res.data.name || "Staff"}`);
        navigate("/admin", { replace: true });
      } else {
        const isAdmin = res.data.role === "super_admin" || res.data.role === "florist_admin";
        toast.success(id ? "Berhasil masuk" : "Logged in");
        navigate(isAdmin ? "/admin" : "/home", { replace: true });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || (id ? "Terjadi kesalahan" : "Something went wrong"));
    }
    setLoading(false);
  };

  const staff = portal === "staff";

  return (
    <div className="dark-immersive min-h-screen relative grid lg:grid-cols-2" data-testid="login-page">
      <CustomCursor />
      {/* LEFT: visual panel */}
      <div className="hidden lg:block relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center transition-all duration-700"
          style={{
            backgroundImage: staff
              ? "linear-gradient(135deg, rgba(18,14,13,0.7), rgba(18,14,13,0.55)), url(https://images.unsplash.com/photo-1490806843957-31f4c9a91c65?w=1600&q=80)"
              : "linear-gradient(135deg, rgba(18,14,13,0.55), rgba(18,14,13,0.4)), url(https://images.unsplash.com/photo-1563207153-f403bf289096?w=1600&q=80)"
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-seikat-ink via-transparent to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div onClick={() => navigate("/")} className="inline-flex items-center cursor-pointer w-fit" data-testid="login-wordmark" data-magnet>
            <span className="font-heading text-3xl tracking-tight text-seikat-bone">
              Seikat<span className="text-seikat-pink">.</span>
            </span>
          </div>
          <motion.div
            key={staff ? "staff-panel" : "customer-panel"}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE }}
          >
            {staff ? (
              <>
                <p className="eyebrow-immersive" style={{ color: "#C5DB7B" }}>Atelier Staff</p>
                <h2 className="font-heading text-5xl text-seikat-bone mt-3 leading-tight">
                  {id ? <>Selamat datang, <em>tim kami.</em></> : <>Welcome back, <em>our team.</em></>}
                </h2>
                <p className="font-body text-seikat-bone/55 mt-4 text-sm leading-relaxed">
                  {id ? "Hanya akses untuk tim internal Seikat." : "Restricted access for Seikat internal team."}
                </p>
              </>
            ) : (
              <>
                <p className="eyebrow-immersive">Seikat Atelier</p>
                <h2 className="font-heading text-5xl text-seikat-bone mt-3 leading-tight">
                  {id ? <>Selamat datang, <em>kekasih bunga.</em></> : <>Welcome back, <em>flower friend.</em></>}
                </h2>
                <p className="font-body text-seikat-bone/55 mt-4 text-sm leading-relaxed">
                  {id ? "Setiap bouquet, sebuah cerita yang menunggu diceritakan." : "Every bouquet holds a story waiting to be told."}
                </p>
              </>
            )}
          </motion.div>
        </div>
      </div>

      {/* RIGHT: form panel */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="flex flex-col justify-center px-6 sm:px-12 lg:px-16 xl:px-24 py-16 min-h-screen"
      >
        {/* Mobile logo */}
        <div className="lg:hidden mb-10">
          <span className="font-heading text-3xl text-seikat-bone">Seikat<span className="text-seikat-pink">.</span></span>
        </div>

        {/* Portal toggle */}
        <div className="flex gap-1 p-1 rounded-full border border-white/10 bg-white/5 w-fit mb-10" data-testid="portal-toggle">
          <button
            onClick={() => { setPortal("customer"); setMode("login"); }}
            data-testid="portal-customer"
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs tracking-widest uppercase transition-all ${
              portal === "customer" ? "bg-seikat-pink text-seikat-ink" : "text-seikat-bone-muted hover:text-seikat-bone"
            }`}
          >
            <Heart size={11} /> {id ? "Pelanggan" : "Customer"}
          </button>
          <button
            onClick={() => { setPortal("staff"); setMode("login"); }}
            data-testid="portal-staff"
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs tracking-widest uppercase transition-all ${
              portal === "staff" ? "bg-seikat-lime text-seikat-ink" : "text-seikat-bone-muted hover:text-seikat-bone"
            }`}
          >
            <ShieldCheck size={11} /> {id ? "Staff" : "Staff"}
          </button>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={portal + mode}
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.35, ease: EASE }}
          >
            <div className="mb-8">
              <h1 className="font-heading text-4xl sm:text-5xl text-seikat-bone leading-[0.95]">
                {staff
                  ? (id ? <>Portal <em>Staff</em>.</> : <>Staff <em>Portal</em>.</>)
                  : (mode === "login"
                    ? (id ? <>Masuk<em>.</em></> : <>Sign <em>in.</em></>)
                    : (id ? <>Buat <em>akun.</em></> : <>Create <em>account.</em></>)
                  )}
              </h1>
              <p className="font-body text-seikat-bone/50 mt-3 text-sm">
                {staff
                  ? (id
                    ? "Hanya akun dengan peran staff/admin yang dapat masuk lewat portal ini."
                    : "Only accounts with staff/admin role can sign in through this portal.")
                  : (mode === "register"
                    ? (id ? "Sudah punya akun? " : "Already have an account? ")
                    : "")}
                {!staff && mode === "register" && (
                  <button onClick={() => setMode("login")} className="text-seikat-pink underline underline-offset-4">
                    {id ? "Masuk" : "Sign in"}
                  </button>
                )}
              </p>
            </div>

            <div className="space-y-4 max-w-sm">
              {portal === "customer" && mode === "register" && (
                <Field icon={User} label={id ? "Nama Lengkap" : "Full Name"}>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder={id ? "Nama kamu" : "Your name"}
                    className="immersive-input"
                    data-testid="register-name"
                    autoFocus
                  />
                </Field>
              )}

              <Field icon={Mail} label="Email">
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  onKeyDown={e => e.key === "Enter" && handleSubmit()}
                  placeholder={staff ? "staff@seikat.id" : "you@email.com"}
                  className="immersive-input"
                  data-testid="login-email"
                  autoFocus={portal === "customer" && mode === "login"}
                />
              </Field>

              <Field icon={Lock} label={id ? "Kata Sandi" : "Password"}>
                <input
                  type="password"
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  onKeyDown={e => e.key === "Enter" && handleSubmit()}
                  placeholder="••••••••"
                  className="immersive-input"
                  data-testid="login-password"
                  autoComplete={mode === "register" ? "new-password" : "current-password"}
                />
              </Field>

              {/* Forgot password link — customer login only */}
              {portal === "customer" && mode === "login" && (
                <div className="flex justify-end">
                  <Link
                    to="/forgot-password"
                    className="text-xs font-body text-seikat-bone-muted hover:text-seikat-pink transition-colors tracking-wide"
                    data-testid="forgot-password-link"
                  >
                    {id ? "Lupa kata sandi?" : "Forgot password?"}
                  </Link>
                </div>
              )}

              <button
                onClick={handleSubmit}
                disabled={loading}
                data-testid="login-submit-btn"
                data-magnet
                className="btn-pill btn-pill--solid w-full justify-center mt-3"
                style={staff ? { background: "#C5DB7B", color: "#120E0D", borderColor: "#C5DB7B" } : {}}
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                {staff
                  ? (id ? "Masuk sebagai Staff" : "Sign in as Staff")
                  : (mode === "login"
                    ? (id ? "Masuk" : "Sign In")
                    : (id ? "Daftar" : "Create Account"))}
              </button>

              {/* Google OAuth — customer only */}
              {portal === "customer" && (
                <>
                  <div className="flex items-center gap-3 my-2">
                    <div className="flex-1 h-px bg-white/10" />
                    <span className="text-[10px] tracking-[0.25em] uppercase text-seikat-bone-muted">
                      {id ? "atau" : "or"}
                    </span>
                    <div className="flex-1 h-px bg-white/10" />
                  </div>
                  <button
                    onClick={googleLogin}
                    className="btn-pill w-full justify-center"
                    data-testid="google-login-btn"
                    data-magnet
                  >
                    <GoogleIcon /> {id ? "Lanjutkan dengan Google" : "Continue with Google"}
                  </button>
                </>
              )}

              {/* Toggle login/register — customer only */}
              {portal === "customer" && (
                <p className="text-center mt-6 text-sm font-body text-seikat-bone/55">
                  {mode === "login"
                    ? (id ? "Belum punya akun? " : "Don't have an account? ")
                    : (id ? "Sudah punya akun? " : "Already have an account? ")}
                  <button
                    onClick={() => setMode(m => m === "login" ? "register" : "login")}
                    className="text-seikat-pink hover:text-seikat-bone transition-colors underline underline-offset-4"
                    data-testid="toggle-auth-mode"
                    data-magnet
                  >
                    {mode === "login" ? (id ? "Daftar" : "Sign up") : (id ? "Masuk" : "Sign in")}
                  </button>
                </p>
              )}

              {staff && (
                <p className="mt-6 text-xs font-body text-seikat-bone-muted text-center leading-relaxed">
                  {id
                    ? "Lupa akses staff? Hubungi super admin untuk reset."
                    : "Forgot staff access? Contact super admin to reset."}
                </p>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <label className="block">
      <div className="flex items-center gap-1.5 mb-2 font-body text-[10px] tracking-[0.25em] uppercase text-seikat-bone-muted">
        {Icon && <Icon size={11} strokeWidth={1.6} />}
        <span>{label}</span>
      </div>
      {children}
    </label>
  );
}

function GoogleIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}
